import React, { useState } from "react";
import { FaTrash, FaGripVertical } from "react-icons/fa";
import ProductsModal from "../../../Components/ProductsModal/ProductsModal";
import stylez from "./DeliveriesTable.module.css";
import LoadingSpinner from "../../../Components/Shared/Loader/Loader";
import axiosInstance from "../../../Configuration/axiosInstance";

const DeliveriesTable = (props) => {
  const {
    show,
    setShow,
    order,
    prods,
    loading,
    deliveries,
    handleChecked,
    handleProductModal,
    updateDeliveries,
    deleteDelivery,
    count,
    page,
    pageSize,
    handleNext,
    handlePrevious,
    calculateStock,
    headings,
    refreshDeliveries, // Add this prop for refreshing data
  } = props;

  // ✅ NEW: Drag and drop state
  const [draggedOrder, setDraggedOrder] = useState(null);
  const [draggedOverIndex, setDraggedOverIndex] = useState(null);
  const [reorderLoading, setReorderLoading] = useState(null);

  // ✅ NEW: Handle drag start
  const handleDragStart = (e, orderItem, orderIndex, deliveryId) => {
    setDraggedOrder({ orderItem, orderIndex, deliveryId });
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/html", e.target.outerHTML);
  };

  // ✅ NEW: Handle drag over
  const handleDragOver = (e, index) => {
    e.preventDefault();
    setDraggedOverIndex(index);
    e.dataTransfer.dropEffect = "move";
  };

  // ✅ NEW: Handle drag leave
  const handleDragLeave = () => {
    setDraggedOverIndex(null);
  };

  // ✅ NEW: Handle drop
  const handleDrop = async (e, targetIndex, deliveryId) => {
    e.preventDefault();
    setDraggedOverIndex(null);

    if (!draggedOrder || draggedOrder.deliveryId !== deliveryId) {
      setDraggedOrder(null);
      return;
    }

    const { orderIndex: sourceIndex } = draggedOrder;

    if (sourceIndex === targetIndex) {
      setDraggedOrder(null);
      return;
    }

    try {
      setReorderLoading(deliveryId);

      // Find the delivery
      const delivery = deliveries.find((d) => d._id === deliveryId);
      if (!delivery) throw new Error("Delivery not found");

      console.log("=== REORDERING DEBUG ===");
      console.log("Delivery structure:", delivery);
      console.log("Orders in delivery:", delivery.order);

      // Create new order array with reordered items
      const ordersArray = [...delivery.order];
      const [movedOrder] = ordersArray.splice(sourceIndex, 1);
      ordersArray.splice(targetIndex, 0, movedOrder);

      // ✅ FIXED: Extract the correct order IDs from the new structure
      const orderIds = ordersArray.map((orderItem) => {
        // Handle the new structure: { orderId: ObjectId, displayOrder: Number }
        if (orderItem.orderId) {
          // If orderId is populated (object), get its _id
          if (typeof orderItem.orderId === 'object' && orderItem.orderId._id) {
            return orderItem.orderId._id;
          } else {
            // If orderId is just an ObjectId string
            return orderItem.orderId;
          }
        }
        console.error("Unexpected order structure:", orderItem);
        return null;
      }).filter(Boolean);

      console.log("Extracted order IDs for reorder:", orderIds);

      // Call backend to update order
      const response = await axiosInstance.patch(`/delivery/${deliveryId}/reorder-orders`, {
        orderIds,
      });

      console.log("Reorder response:", response.data);

      // Refresh deliveries data
      if (refreshDeliveries) {
        await refreshDeliveries();
      } else {
        window.location.reload();
      }
      
    } catch (error) {
      console.error("Error reordering orders:", error);
      const errorMessage = error.response?.data?.message || "Failed to reorder orders. Please try again.";
      alert(`Error: ${errorMessage}`);
    } finally {
      setReorderLoading(null);
      setDraggedOrder(null);
    }
  };

  return (
    <>
      <div className={stylez.tableDiv}>
        {show && (
          <ProductsModal
            show={show}
            setShow={setShow}
            update={true}
            order={order}
            products={prods}
            title={"Products in the Current Order"}
          />
        )}

        <table className={stylez.tableStyles} bordercolor="black">
          <thead className={stylez.tableHeader}>
            <tr>
              {headings.map((i, key) => (
                <th key={key}>{i}</th>
              ))}
            </tr>
          </thead>
          <tbody className={stylez.deliveryBody}>
            {loading ? (
              <tr>
                <td colSpan="5" style={{ textAlign: "center" }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      minHeight: "200px",
                    }}
                  >
                    <LoadingSpinner />
                  </div>
                </td>
              </tr>
            ) : (
              deliveries?.map((delivery, deliveryIndex) => {
                // ✅ FIXED: Calculate products count from new structure
                const allProductsCount = delivery?.order?.reduce(
                  (acc, orderItem) => {
                    // Handle new structure: orderItem.orderId.products
                    const products = orderItem.orderId?.products || [];
                    return acc + products.length;
                  },
                  0
                );
                
                return (
                  <tr key={deliveryIndex}>
                    <td>
                      <input
                        type="checkbox"
                        onChange={(e) => handleChecked(e, delivery._id)}
                        id={delivery._id}
                        value={delivery._id}
                      />
                    </td>

                    <td>{delivery?.run}</td>
                    <td>{delivery?.vehicle}</td>
                    
                    {/* ✅ ENHANCED: Nested table with drag-and-drop support */}
                    <td>
                      <table className={stylez.nestedBody}>
                        <thead>
                          <tr>
                            <th style={{ width: "30px" }}>📍</th>
                            <th>Order Number</th>
                            <th>Business Name</th>
                            <th>Sku's</th>
                            <th>Order Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {delivery?.order
                            ?.sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0)) // ✅ Sort by displayOrder
                            ?.map((orderItem, orderIndex) => {
                              // ✅ FIXED: Handle new structure
                              const order = orderItem.orderId; // The actual order data is in orderId
                              
                              return (
                                <tr
                                  key={orderItem._id || orderIndex}
                                  draggable
                                  onDragStart={(e) => handleDragStart(e, orderItem, orderIndex, delivery._id)}
                                  onDragOver={(e) => handleDragOver(e, orderIndex)}
                                  onDragLeave={handleDragLeave}
                                  onDrop={(e) => handleDrop(e, orderIndex, delivery._id)}
                                  className={`${stylez.draggableRow} ${
                                    draggedOrder?.orderItem._id === orderItem._id ? stylez.dragging : ""
                                  } ${
                                    draggedOverIndex === orderIndex ? stylez.dragOver : ""
                                  }`}
                                  style={{
                                    opacity: draggedOrder?.orderItem._id === orderItem._id ? 0.5 : 1,
                                    cursor: "move",
                                  }}
                                >
                                  <td style={{ width: "30px", textAlign: "center" }}>
                                    <FaGripVertical 
                                      className={stylez.dragHandle}
                                      title="Drag to reorder"
                                    />
                                  </td>
                                  <td style={{ width: "100px" }}>
                                    {order?.orderNumber}
                                  </td>
                                  <td style={{ width: "200px" }}>
                                    {order?.customer?.businessName}
                                  </td>
                                  <td style={{ width: "20px" }}>
                                    <button
                                      className="popupBtn"
                                      onClick={() => handleProductModal(order?.products, order)}
                                    >
                                      {order?.products?.length || 0}
                                    </button>
                                  </td>
                                  <td style={{ width: "20px" }}>
                                    {order?.date && new Date(order.date).toLocaleDateString("en-GB")}
                                  </td>
                                </tr>
                              );
                            })}
                        </tbody>
                      </table>
                      
                      {/* ✅ Show loading indicator during reorder */}
                      {reorderLoading === delivery._id && (
                        <div className={stylez.reorderLoading}>
                          <LoadingSpinner size="small" />
                          <span>Updating order...</span>
                        </div>
                      )}
                    </td>

                    <td style={{ width: "125px" }}>
                      {new Date(delivery?.date).toLocaleDateString("en-GB")}
                    </td>

                    {/* Add the new Assigned Drivers column */}
                    <td style={{ width: "150px", padding: "8px" }}>
                      {delivery?.assignedUsers?.length > 0 ? (
                        <div>
                          {delivery.assignedUsers.map((user, userIndex) => (
                            <div key={userIndex}>
                              {user.name || user.email.split('@')[0]}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div style={{ color: "#6c757d", fontStyle: "italic" }}>
                          No drivers assigned
                        </div>
                      )}
                    </td>

                    {headings.includes("Status") ? <td>{delivery?.status}</td> : null}

                    {headings.includes("Actions") ? (
                      <td style={{ width: "100px" }}>
                        <div className="actionButtons">
                          <button
                            onClick={() => {
                              // ✅ FIXED: Extract all products from new structure
                              const allProducts = delivery?.order?.flatMap((orderItem) => 
                                orderItem.orderId?.products || []
                              ) || [];
                              handleProductModal(allProducts, null);
                            }}
                            className="popupBtn"
                          >
                            View All Products ({allProductsCount})
                          </button>
                          <button
                            onClick={() => {
                              updateDeliveries(delivery);
                            }}
                            className="editBtn"
                          >
                            Update Deliveries
                          </button>

                          <button
                            onClick={() =>
                              window.confirm(
                                "Are you sure you want to delete this Delivery?"
                              ) && deleteDelivery(delivery._id)
                            }
                            className="deleteBtn"
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </td>
                    ) : null}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      {calculateStock ? null : (
        <div className={stylez.pagination}>
          <button
            disabled={page === 1}
            onClick={handlePrevious}
            className={stylez.pageButton}
          >
            Previous
          </button>
          <span className={stylez.pages}>
            Page -<span className={stylez.pageNumber}>{page} </span>
          </span>
          <button
            disabled={count < pageSize}
            onClick={handleNext}
            className={stylez.pageButton}
          >
            Next
          </button>
        </div>
      )}
    </>
  );
};

export default DeliveriesTable;